import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SearchBar from "../SearchFilter";
import { formatDateLabel, formatTime, getLocalDate, timeAgo } from "../../services/getLocalDate";


// Add more reference_type → route mappings here as needed
const REFERENCE_ROUTES = {
    leave: "/leaves",
    address_change: "/location-list", // ← update to your actual path
};

// ✅ Reliable unread check — API sends 0 (number) not false
const isUnread = (n) => n.is_read === 0 || n.is_read === false;

export default function NotificationRightSide({
    notifications,
    onClose,
    onMarkOneRead,
    onDelete,
    onOpenTimeline,
}) {
    const modalRef = useRef(null);
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState("");

    console.log("notification", notifications);

    // Close on Escape
    useEffect(() => {
        const handleKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [onClose]);

    // Prevent body scroll
    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = ""; };
    }, []);

    // ✅ Count uses the same isUnread() helper — always consistent
    const unreadCount = notifications.filter(isUnread).length;

    // Search filter
    const filteredNotifications = notifications.filter(n =>
        n.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        n.message?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Group by date
    const groupNotifications = (data) => {
        const groups = { Today: [], Yesterday: [], Older: {} };

        const today = getLocalDate(new Date());

        const yestDate = new Date();
        yestDate.setDate(yestDate.getDate() - 1);
        const yesterday = getLocalDate(yestDate);

        data.forEach((n) => {
            const raw = n.created_at || n.date;

            if (!raw) {
                groups.Today.push(n);
                return;
            }

            const notifDate = getLocalDate(raw);

            if (notifDate === today) {
                groups.Today.push(n);
            } else if (notifDate === yesterday) {
                groups.Yesterday.push(n);
            } else {
                if (!groups.Older[notifDate]) groups.Older[notifDate] = [];
                groups.Older[notifDate].push(n);
            }
        });

        return groups;
    };

    const grouped = groupNotifications(filteredNotifications);

    // ── Handle click ──────────────────────────────────────────────
    const handleItemClick = (n) => {
        // ✅ Always call mark-read if still unread — badge drops by 1 immediately
        if (isUnread(n)) {
            onMarkOneRead(n.id);
        }

        const route = REFERENCE_ROUTES[n.reference_type];
        if (route) {
            // leave / address_change → navigate, no timeline
            onClose();
            navigate(route);
            return;
        }

        // All other types → open timeline
        const ref = n.reference_details;
        const timelineItems = ref
            ? buildTimeline(n.reference_type, ref)
            : [{ time: "—", text: n.message }];
        onOpenTimeline(timelineItems);
    };

    function buildTimeline(type, ref) {
        return [
            ref.created_at && { time: formatTime(ref.created_at), text: "Request created" },
            ref.updated_at && { time: formatTime(ref.updated_at), text: "Last updated" },
        ].filter(Boolean);
    }

    function fmtTime(str) {
        if (!str) return null;
        const d = new Date(str);
        return isNaN(d) ? str : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
    }

    function statusColor(status) {
        if (!status) return "badge status-pending";

        const s = status.toString().trim().toLowerCase();

        if (s === "pending") return "badge status-pending";
        if (s === "approved") return "badge status-approved";
        if (s === "rejected") return "badge status-rejected";

        return "";
    }

    function typeIcon(reference_type) {
        if (reference_type === "leave") return "📋";
        if (reference_type === "address_change") return "📍";
        return "👤";
    }

    // ── Render one notification ───────────────────────────────────
    const renderItem = (n) => {
        const unread = isUnread(n); // ✅ use helper, not !n.is_read
        const ref = n.reference_details;
        const leaveStatus = ref?.approve_or_reject_flag || ref?.approve_or_reject_status;

        return (
            <li
                key={n.id}
                className={`nm-item ${unread ? "nm-item--unread" : ""}`}
                onClick={() => handleItemClick(n)}
            >
                {/* Avatar */}
                <div className="nm-avatar">
                    <span>{typeIcon(n.reference_type)}</span>
                </div>

                {/* Content */}
                <div className="nm-content">
                    <div className="nm-item-top">
                        <p className="nm-item-title">{n.user_name} ( {n.title} )</p>
                        <span className="nm-item-time">Date: {formatDateLabel(n.created_at)}, {timeAgo(n.created_at)}</span>
                    </div>
                    <p className="nm-item-msg">{n.message}</p>

                    {/* Leave strip */}
                    {n.reference_type === "leave" && ref && (
                        <div className="nm-info-strip">
                            <span className="statuschange">Date: {formatDateLabel(ref.from_date)}
                                {ref.to_date && ref.to_date !== ref.from_date
                                    ? ` → ${formatDateLabel(ref.to_date)}`
                                    : ""}</span>
                            {ref.leave_duration && <span className="statuschange">Duration: {ref.leave_duration}</span>}
                            {ref.total_days && <span className="statuschange">Total Day: {ref.total_days} day{ref.total_days > 1 ? "s" : ""}</span>}
                            {/* {ref.reason_description && <span className="statuschange">Reason: {ref.reason_description}</span>} */}
                            {leaveStatus && (
                                <span className={`ms-2 fw-semibold ${statusColor(leaveStatus)}`}>{leaveStatus}</span>
                            )}
                        </div>
                    )}

                    {/* Address change strip */}
                    {n.reference_type === "address_change" && ref && (
                        <div className="nm-info-strip">
                            <span className="statuschange">
                                Address Change Date: {formatDateLabel(ref.from_date)}
                                {ref.to_date && ref.to_date !== ref.from_date
                                    ? ` → ${formatDateLabel(ref.to_date)}`
                                    : ""}
                            </span>
                            {ref.approve_or_reject_status && (
                                <span className={`ms-2 fw-semibold ${statusColor(ref.approve_or_reject_status)}`}>
                                    {ref.approve_or_reject_status}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="nm-item-actions">
                    {unread && <span className="nm-unread-dot" />}
                    <button
                        className="nm-delete"
                        title="Dismiss"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(n.id);
                        }}
                    >
                        ✕
                    </button>
                </div>
            </li>
        );
    };

    return (
        <div className="nm-overlay" onClick={onClose}>
            <div
                className="nm-modal"
                ref={modalRef}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Notifications"
            >
                {/* Header */}
                <div className="nm-header">
                    <div className="nm-header-left">
                        <span className="nm-bell">🔔</span>
                        <div>
                            <h2 className="nm-title">Notifications</h2>
                            {/* ✅ This subtitle also updates live as you click items */}
                            <p className="nm-subtitle">
                                {unreadCount > 0
                                    ? `${unreadCount} unread message${unreadCount > 1 ? "s" : ""}`
                                    : "All caught up!"}
                            </p>
                        </div>
                    </div>
                    <div className="nm-header-right">
                        <button className="nm-close-btn" onClick={onClose}>✕</button>
                    </div>
                </div>

                {/* Search */}
                <SearchBar
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    onClear={() => setSearchQuery("")}
                />

                {/* List */}
                <ul className="nm-list">
                    {filteredNotifications.length === 0 ? (
                        <li className="nm-empty">
                            <div className="nm-empty-icon">🎉</div>
                            <p className="nm-empty-title">No notifications</p>
                            <p className="nm-empty-sub">You're fully up to date.</p>
                        </li>
                    ) : (
                        <>
                            {grouped.Today.length > 0 && (
                                <><li className="nm-group-title">Today</li>{grouped.Today.map(renderItem)}</>
                            )}
                            {grouped.Yesterday.length > 0 && (
                                <><li className="nm-group-title">Yesterday</li>{grouped.Yesterday.map(renderItem)}</>
                            )}
                            {Object.keys(grouped.Older).map(date => (
                                <div key={date}>
                                    <li className="nm-group-title">{formatDateLabel(date)}</li>
                                    {grouped.Older[date].map(renderItem)}
                                </div>
                            ))}
                        </>
                    )}
                </ul>
            </div>
        </div>
    );
}